import React, { useState, useEffect } from 'react';
import { formatMonthForDisplay } from '../utils/monthUtils';
import { X, Copy } from 'lucide-react';

const MonthlyArtworkModal = ({
  isOpen,
  onClose,
  monthlyArtwork = [],
  onMonthlyArtworkChange,
  isOngoing = false,
}) => {
  const [localMonthlyArtwork, setLocalMonthlyArtwork] = useState([]);
  const [localIsOngoing, setLocalIsOngoing] = useState(false);
  const [showClosedIssues, setShowClosedIssues] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const filteredArtwork = monthlyArtwork.filter(month => month.month);
      const firstSelectedOrNotClosedMonthIndex = filteredArtwork.findIndex((month) => month.isSelected || !month.isClosed);
      setLocalMonthlyArtwork([...filteredArtwork.slice(firstSelectedOrNotClosedMonthIndex ?? 0)]);
      setLocalIsOngoing(isOngoing);
    }
  }, [isOpen, monthlyArtwork, isOngoing]);

  const handleMonthToggle = index => {
    const currentItem = localMonthlyArtwork[index];

    if (currentItem.isClosed) {
      return;
    }

    const updated = [...localMonthlyArtwork];
    updated[index] = {
      ...updated[index],
      isSelected: !updated[index].isSelected,
    };
    setLocalMonthlyArtwork(updated);
  };

  // Ongoing = book every open issue. Turning it on selects all open months
  // (closed issues stay excluded); turning it off leaves selections untouched
  // so the user keeps whatever they had ticked.
  const handleOngoingToggle = () => {
    const next = !localIsOngoing;
    setLocalIsOngoing(next);

    if (next) {
      setLocalMonthlyArtwork(
        localMonthlyArtwork.map(item => ({
          ...item,
          isSelected: !item.isClosed,
        }))
      );
    }
  };

  const handleArtworkNoteChange = (index, note) => {
    const currentItem = localMonthlyArtwork[index];
    
    if (currentItem.isClosed) {
      return;
    }
    
    const updated = [...localMonthlyArtwork];
    updated[index] = {
      ...updated[index],
      artworkNote: note,
    };
    setLocalMonthlyArtwork(updated);
  };

  const handleFilenameChange = (index, filename) => {
    const currentItem = localMonthlyArtwork[index];
    
    if (currentItem.isClosed) {
      return;
    }
    
    const updated = [...localMonthlyArtwork];
    updated[index] = {
      ...updated[index],
      filename: filename || '',
    };
    setLocalMonthlyArtwork(updated);
  };

  const handleSave = () => {
    onMonthlyArtworkChange(localMonthlyArtwork, localIsOngoing);
    onClose();

    import('react-toastify').then(({ toast }) => {
      toast.warning('Artwork details updated. Booking must also be saved.', {
        position: 'top-right',
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          fontSize: '16px',
          fontWeight: '500',
        },
      });
    });
  };

  const handleCancel = () => {
    setLocalMonthlyArtwork([...monthlyArtwork]);
    setLocalIsOngoing(isOngoing);
    onClose();
  };

  const handleCopyDown = fromIndex => {
    const sourceItem = localMonthlyArtwork[fromIndex];

    const updated = localMonthlyArtwork.map((item, index) => {
      if (item.isSelected && index > fromIndex) {
        return {
          ...item,
          artworkNote: sourceItem.artworkNote || '',
          filename: sourceItem.filename || '',
        };
      }
      return item;
    });

    setLocalMonthlyArtwork(updated);
  };

  const shouldShowCopyButton = index => {
    if (!localMonthlyArtwork[index]?.isSelected) {
      return false;
    }

    const hasSelectedRowBelow = localMonthlyArtwork.slice(index + 1).some(item => item.isSelected);

    return hasSelectedRowBelow;
  };

  if (!isOpen) return null;

  const selectedCount = localMonthlyArtwork.filter(item => item.isSelected).length;

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Select Issues & Add Artwork Details
            </h2>
            <p className="mt-1 text-sm text-gray-500 text-wrap">
              {localIsOngoing
                ? 'This is an ongoing booking - all open issues are automatically selected. You can uncheck open issues if needed. Closed issues are excluded.'
                : 'Choose which issues to include. Closed issues cannot be selected.'}
            </p>
          </div>
          <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Controls - stay fixed above the scrolling rows */}
        <div className="px-6 pt-6 pb-4">
          <div className="grid grid-cols-3 items-center">
            <label className="flex items-center text-sm text-gray-700 justify-self-start">
              <input
                type="checkbox"
                checked={localIsOngoing}
                onChange={handleOngoingToggle}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                title="Ongoing booking - selects all open issues (closed issues are excluded)"
              />
              <span className="ml-2">Ongoing</span>
            </label>
            <p className="text-sm text-center text-gray-600 justify-self-center">
              {selectedCount} months selected
            </p>
            <label className="flex items-center text-sm text-gray-700 justify-self-end">
              <input
                type="checkbox"
                checked={showClosedIssues}
                onChange={() => setShowClosedIssues(!showClosedIssues)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                title="Show closed issues"
              />
              <span className="ml-2">Show closed issues</span>
            </label>
          </div>
        </div>

        {/* Table - only the rows scroll; the header row stays via sticky thead */}
        <div className="px-6 pb-6 overflow-auto max-h-[60vh]">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th className="sticky top-0 z-10 px-4 py-3 w-16 text-xs font-medium tracking-wide text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-300">
                    Select
                  </th>
                  <th className="sticky top-0 z-10 px-4 py-3 w-24 text-xs font-medium tracking-wide text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-300">
                    Issues
                  </th>
                  <th className="sticky top-0 z-10 px-4 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-300">
                    Note
                  </th>
                  <th className="sticky top-0 z-10 px-4 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-300">
                    Filename
                  </th>
                  <th className="sticky top-0 z-10 px-4 py-3 w-32 text-xs font-medium tracking-wide text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-300"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {localMonthlyArtwork.map((item, index) => {
                  const isClosedIssue = item.isClosed || false;
                  const isCheckboxDisabled = isClosedIssue;
                  const isFieldDisabled = isClosedIssue;
                  const showCopyButton = shouldShowCopyButton(index);

                  return (
                    <tr key={item.month} className={`${item.isSelected ? (isClosedIssue ? 'bg-gray-50' : 'bg-blue-50') : (isClosedIssue ? 'bg-red-50' : '')} ${isClosedIssue && !showClosedIssues ? 'hidden' : ''}`}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          id={`month-${item.month}-${index}`}
                          checked={item.isSelected}
                          onChange={() => handleMonthToggle(index)}
                          disabled={isCheckboxDisabled}
                          className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${isCheckboxDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                          title={
                            isClosedIssue
                              ? 'Closed issue - cannot be selected or deselected'
                              : ''
                          }
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <label
                          htmlFor={`month-${item.month}-${index}`}
                          className="text-sm font-medium text-gray-900 cursor-pointer"
                        >
                          {formatMonthForDisplay(item.month)}
                        </label>
                      </td>
                      <td className="px-4 py-4">
                        <input
                          type="text"
                          placeholder={
                            !item.isSelected ? 'Select issue first' : isFieldDisabled ? 'Closed issue' : 'Enter artwork note...'
                          }
                          value={item.artworkNote || ''}
                          onChange={e => handleArtworkNoteChange(index, e.target.value)}
                          disabled={!item.isSelected || isFieldDisabled}
                          className={`w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                            !item.isSelected || isFieldDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                          }`}
                        />
                      </td>
                      <td className="px-4 py-4">
                        <input
                          type="text"
                          placeholder={!item.isSelected ? 'Select issue first' : isFieldDisabled ? 'Closed issue' : 'Enter filename...'}
                          value={item.filename || ''}
                          onChange={e => handleFilenameChange(index, e.target.value)}
                          disabled={!item.isSelected || isFieldDisabled}
                          className={`w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                            !item.isSelected || isFieldDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                          }`}
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {showCopyButton && (
                          <button
                            type="button"
                            onClick={() => handleCopyDown(index)}
                            className="flex gap-1.5 items-center px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-md transition-colors hover:bg-blue-100"
                            title="Copy this row's values to all selected issues below"
                          >
                            <Copy className="w-3.5 h-3.5" />
                            Copy down
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {selectedCount > 0 ? `${selectedCount} months selected` : 'No months selected'}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyArtworkModal;
